/**
 * Pending Invites List
 *
 * Displays pending organization invites and allows accepting/declining them.
 */

import { useState } from "react";
import { Check, X, Building2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import { useRespondToInvite } from "@/hooks/useOrganization";
import type { OrganizationInvite } from "@/types/organization";

export function PendingInvitesList() {
  const { pendingInvites, hasOrganization, refetchUser, refetchOrganization, refetchInvites } =
    useOrganizationContext();
  const respondMutation = useRespondToInvite();
  const [confirmInvite, setConfirmInvite] = useState<OrganizationInvite | null>(null);
  const [respondingInviteId, setRespondingInviteId] = useState<number | null>(null);
  const [hiddenInviteIds, setHiddenInviteIds] = useState<Set<number>>(new Set());

  // Filter out hidden invites for immediate UI feedback
  const visibleInvites = pendingInvites.filter((invite) => !hiddenInviteIds.has(invite.id));

  const handleAccept = async (invite: OrganizationInvite) => {
    // If user already has an organization, show confirmation dialog
    if (hasOrganization) {
      setConfirmInvite(invite);
      return;
    }

    await acceptInvite(invite.id);
  };

  const acceptInvite = async (inviteId: number) => {
    setRespondingInviteId(inviteId);
    try {
      await respondMutation.mutateAsync({
        inviteId,
        data: { status: "ACCEPTED" },
      });
      // The mutation's onSuccess handles refetching - user should now have organization_id
      // which will cause NoOrganizationGuard to show the dashboard
    } catch (error) {
      console.error("Failed to accept invite:", error);
    } finally {
      setRespondingInviteId(null);
      setConfirmInvite(null);
    }
  };

  const handleDecline = async (inviteId: number) => {
    setRespondingInviteId(inviteId);
    // Immediately hide the invite from UI (optimistic update)
    setHiddenInviteIds((prev) => new Set([...prev, inviteId]));
    try {
      await respondMutation.mutateAsync({
        inviteId,
        data: { status: "DECLINED" },
      });
    } catch (error) {
      console.error("Failed to decline invite:", error);
      // On error, show the invite again
      setHiddenInviteIds((prev) => {
        const next = new Set(prev);
        next.delete(inviteId);
        return next;
      });
    } finally {
      setRespondingInviteId(null);
    }
  };

  if (visibleInvites.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-4">
        No pending invitations.
      </p>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {visibleInvites.map((invite) => (
          <Card key={invite.id}>
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {invite.organization_name || `Organization #${invite.organization_id}`}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Invited {new Date(invite.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDecline(invite.id)}
                  disabled={respondingInviteId === invite.id}
                >
                  <X className="h-4 w-4 mr-1" />
                  Decline
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleAccept(invite)}
                  disabled={respondingInviteId === invite.id}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Accept
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Confirmation dialog for users already in an organization */}
      <Dialog open={!!confirmInvite} onOpenChange={() => setConfirmInvite(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Leave Current Organization?
            </DialogTitle>
            <DialogDescription>
              You already belong to an organization. Accepting this invitation will remove you from
              your current organization. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmInvite(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => confirmInvite && acceptInvite(confirmInvite.id)}
            >
              Leave & Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
